import React, { useEffect, useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  Sun, Dashboard, Users, MessageQ, AlertCircle, Tool,
  Receipt, Package, IdBadge, FileText, Settings, LogOut, Wifi,
  Wallet, BarChart2, Calendar, FileCheck, UserCheck, Shield, Wrench, ClipCheck,
} from './Icons';
import { salesApi, assignmentsApi } from '../api/client';

function SbLink({ to, icon: Icon, label, badge }) {
  return (
    <NavLink to={to} className={({ isActive }) => `sb-item${isActive ? ' active' : ''}`}>
      <Icon s={16}/>{label}
      {badge != null && <span className="sb-badge">{badge}</span>}
    </NavLink>
  );
}

export function Sidebar() {
  const { user, logout, isAdmin, isSA, isField } = useAuth();
  const navigate = useNavigate();
  const initials = user?.name?.split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase() || '??';
  const [fupBadge, setFupBadge]   = useState(null);
  const [asnBadge, setAsnBadge]   = useState(null);

  useEffect(() => {
    if (!isAdmin) return;
    const today = new Date().toISOString().split('T')[0];
    const lsData = (() => { try { return JSON.parse(localStorage.getItem('sakthi_fup') || '{}'); } catch { return {}; } })();
    salesApi.list({ page_size: 500 }).then(({ data }) => {
      const count = data.filter(s => {
        if (!(s.payment_status === 'partial' || s.payment_status === 'pending')) return false;
        const fupDate = lsData[s.id]?.followup_date || s.followup_date;
        return fupDate && fupDate <= today;
      }).length;
      setFupBadge(count > 0 ? count : null);
    }).catch(() => {});
  }, [isAdmin]);

  useEffect(() => {
    assignmentsApi.list().then(({ data }) => {
      setAsnBadge(data.length > 0 ? data.length : null);
    }).catch(() => {});
  }, []);

  return (
    <aside className="sidebar">
      <div className="sb-brand">
        <div className="sb-brand-icon"><Sun s={18} color="#fff"/></div>
        <div>
          <div className="sb-brand-name">Sakthi Solar</div>
          <div className="sb-brand-sub">Solutions ERP</div>
        </div>
      </div>

      <div className="sb-scroll">
        <div className="sb-group">
          <div className="sb-group-label">Main</div>
          <SbLink to="/dashboard"  icon={Dashboard}   label="Dashboard"/>
          <SbLink to="/customers"  icon={Users}        label="Customers"/>
          <SbLink to="/enquiry"    icon={MessageQ}     label="Enquiry"/>
          <SbLink to="/complaints" icon={AlertCircle}  label="Complaints"/>
        </div>
        <div className="sb-group">
          <div className="sb-group-label">Operations</div>
          <SbLink to="/service"      icon={Tool}       label="Service"/>
          <SbLink to="/sales"        icon={Receipt}    label="Sales & Billing"/>
          <SbLink to="/quotations"   icon={FileCheck}  label="Quotations"/>
          <SbLink to="/inventory"    icon={Package}    label="Inventory"/>
        </div>
        <div className="sb-group">
          <div className="sb-group-label">Field Work</div>
          {isField && <SbLink to="/field-portal"   icon={ClipCheck} label="My Portal"/>}
          <SbLink to="/assignments"    icon={UserCheck} label="Assignments"     badge={asnBadge}/>
          <SbLink to="/service-pickups" icon={Wrench}   label="Service Pickups"/>
        </div>
        <div className="sb-group">
          <div className="sb-group-label">Finance</div>
          {isAdmin && <SbLink to="/expenses"      icon={Wallet}    label="Expenses"/>}
          {isAdmin && <SbLink to="/reports"       icon={BarChart2} label="Reports"/>}
          {isAdmin && <SbLink to="/payment-fups"  icon={Calendar}  label="Payment FUPs" badge={fupBadge}/>}
        </div>
        <div className="sb-group">
          <div className="sb-group-label">Admin</div>
          {isAdmin && <SbLink to="/staff"       icon={IdBadge}  label="Staff"/>}
          {isAdmin && <SbLink to="/attendance"  icon={Calendar} label="Attendance"/>}
          <SbLink to="/templates"   icon={FileText}     label="Templates"/>
          {isAdmin && <SbLink to="/audit-trail" icon={Shield}   label="Audit Trail"/>}
          {isSA   && <SbLink to="/salary-acks"  icon={Wallet}   label="Salary Acks"/>}
          {isSA   && <SbLink to="/daily-wages"  icon={BarChart2} label="Daily Wages"/>}
          {isSA   && <SbLink to="/settings"   icon={Settings}  label="Settings"/>}
        </div>
      </div>

      <div className="sb-foot">
        <div className="sb-user">
          <div className="sb-avatar">{initials}</div>
          <div className="sb-uinfo">
            <div className="sb-uname">{user?.name}</div>
            <div className="sb-urole">{user?.role}</div>
          </div>
          <button className="sb-logout" onClick={() => { logout(); navigate('/login'); }} title="Logout">
            <LogOut s={15}/>
          </button>
        </div>
      </div>
    </aside>
  );
}

export function Topbar({ greeting, sub }) {
  const [time, setTime] = useState('');
  const [date, setDate] = useState('');

  useEffect(() => {
    const tick = () => {
      const now = new Date();
      setTime(now.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false }));
      setDate(now.toLocaleDateString('en-IN', { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' }));
    };
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, []);

  return (
    <div className="topbar">
      <div className="topbar-left">
        {greeting && <div className="topbar-title">{greeting}</div>}
        {sub && <div className="topbar-sub">{sub}</div>}
      </div>
      <div className="topbar-right">
        <div style={{ fontSize: 10, color: 'var(--text3)', textAlign: 'right', lineHeight: 1.3 }}>
          Developed by<br/>
          <span style={{ fontWeight: 600, color: 'var(--text2)' }}>Data Systems Intelligence Pvt Ltd</span>
        </div>
        <div className="topbar-conn"><div className="conn-dot"/><Wifi s={12}/> Connected</div>
        <div className="topbar-time">{date} &nbsp;•&nbsp; {time} (IST)</div>
      </div>
    </div>
  );
}
